import * as Sentry from '@sentry/react'

import { SIX_PWA_EVENT_OFFLINE_READY, SIX_PWA_EVENT_UPDATE_AVAILABLE } from '@/common/storage/events'

export type UpdateServiceWorker = (skipWaiting?: boolean) => Promise<void>

type RegisterServiceWorker = (params: {
    onNeedRefresh: () => void
    onOfflineReady: () => void
    onRegisterError: (error: unknown) => void
}) => UpdateServiceWorker

type ServiceWorkerRegistrationLike = {
    waiting?: unknown
}

type ServiceWorkerContainerLike = {
    getRegistration: () => Promise<ServiceWorkerRegistrationLike | undefined>
}

type SetupServiceWorkerRegistrationParams = {
    registerSW: RegisterServiceWorker
    targetWindow?: Window
    serviceWorker?: ServiceWorkerContainerLike | null
    pathname?: string
    userAgent?: string
    isSecureContext?: boolean
}

function dispatchUpdateAvailable(targetWindow: Window) {
    targetWindow.__sixPwaUpdateAvailable = true
    targetWindow.dispatchEvent(new CustomEvent(SIX_PWA_EVENT_UPDATE_AVAILABLE))
}

// Safari (especially iOS) throws these for environmental reasons outside our control -
// e.g. Private Browsing mode or storage restrictions - rather than an app/SW defect.
const ENVIRONMENTAL_REGISTER_ERROR_NAMES = new Set(['SecurityError', 'QuotaExceededError'])
const ENVIRONMENTAL_REGISTER_ERROR_MESSAGES = new Set(['Internal error'])

// Chromium throws this generic TypeError when it can't fetch sw.js over the network -
// e.g. ad-blockers/privacy extensions, corporate AV/proxy interception, or a flaky
// connection - rather than an app/SW defect. The scope/script URLs in the message vary
// per environment, so match on the stable suffix Chromium always appends.
const ENVIRONMENTAL_REGISTER_ERROR_MESSAGE_SUBSTRINGS = ['An unknown error occurred when fetching the script']

function isEnvironmentalRegisterError(error: unknown): boolean {
    if (!(error instanceof Error)) return false

    return (
        ENVIRONMENTAL_REGISTER_ERROR_NAMES.has(error.name) ||
        ENVIRONMENTAL_REGISTER_ERROR_MESSAGES.has(error.message) ||
        ENVIRONMENTAL_REGISTER_ERROR_MESSAGE_SUBSTRINGS.some((substring) => error.message.includes(substring))
    )
}

function reportRegisterError(
    error: unknown,
    details: { pathname: string; userAgent: string; isSecureContext: boolean },
) {
    try {
        Sentry.captureException(error, {
            level: isEnvironmentalRegisterError(error) ? 'info' : 'error',
            tags: {
                source: 'pwa',
                action: 'service-worker-register',
            },
            extra: details,
        })
    } catch {
        // ignore telemetry failures
    }
}

export function setupServiceWorkerRegistration({
    registerSW,
    targetWindow = window,
    serviceWorker = navigator.serviceWorker,
    pathname = window.location.pathname,
    userAgent = navigator.userAgent,
    isSecureContext = window.isSecureContext,
}: SetupServiceWorkerRegistrationParams) {
    const updateSW = registerSW({
        onNeedRefresh() {
            dispatchUpdateAvailable(targetWindow)
        },
        onOfflineReady() {
            targetWindow.dispatchEvent(new CustomEvent(SIX_PWA_EVENT_OFFLINE_READY))
        },
        onRegisterError(error: unknown) {
            reportRegisterError(error, { pathname, userAgent, isSecureContext })
        },
    })

    if (serviceWorker) {
        serviceWorker
            .getRegistration()
            .then((registration) => {
                if (registration?.waiting) {
                    dispatchUpdateAvailable(targetWindow)
                }
            })
            .catch(() => {
                // ignore
            })
    }

    targetWindow.sixPwaApplyUpdate = async (skipWaiting = true) => {
        try {
            if (!serviceWorker) return false

            const registration = await serviceWorker.getRegistration()
            if (!registration?.waiting) return false

            if (typeof updateSW === 'function') {
                await updateSW(skipWaiting)

                // If updateSW did not cause the waiting worker to activate, try a best-effort
                // postMessage fallback. Some runtime bundles (or older browsers) rely on
                // a message handler in the SW to call `skipWaiting()`.
                try {
                    // After calling updateSW, some runtimes may still leave the
                    // new worker in `waiting` state for a short while. Perform a
                    // best-effort retry loop that posts `SKIP_WAITING` and polls
                    // the registration for activation for up to 10 seconds.
                    // Poll for activation for up to 2 seconds (shorter than production 10s to keep tests fast)
                    const deadline = Date.now() + 2000
                    let activated = false
                    while (Date.now() < deadline && !activated) {
                        const after = await serviceWorker.getRegistration()
                        const waiting = (after as ServiceWorkerRegistrationLike & { waiting?: any })?.waiting
                        if (waiting && typeof waiting.postMessage === 'function') {
                            try {
                                waiting.postMessage({ type: 'SKIP_WAITING' })
                            } catch {
                                // ignore individual postMessage failures
                            }
                        }

                        // If there's a controller (new active worker), consider it activated
                        if (navigator.serviceWorker.controller) {
                            activated = true
                            break
                        }

                        // Small delay before retrying
                        await new Promise((r) => setTimeout(r, 500))
                    }
                } catch {
                    // ignore fallback failures
                }

                return true
            }
        } catch (error) {
            console.warn('apply update failed', error)
        }

        return false
    }

    return updateSW
}
